// @file abstract base class for map implementations

import type {
  Bounds,
  BoundsLiteral,
  GeoMap,
  LatLngLiteral,
  MapDimensions,
  MapOptions,
  MapsEventListener,
  OverlappingMarkerSpiderfier,
  PostMarkerObject,
} from '@@/bits/map'
import {
  getBoundsContainingPositions,
  getBoundsZoomLevel,
  getUniquePositionsFromPositions,
  RELAXED_BOUND,
  RESTRICTED_BOUND,
} from '@@/bits/map'
import type { MarkerColorsHash } from '@@/bits/map_theme'
import { debounce } from 'es-toolkit/compat'

/**
 * Abstract base class for map implementations.
 * Provides a common interface for different map providers (e.g. Google Maps, Leaflet).
 * Handles core map functionality like initialization, navigation, bounds management,
 * marker management and theme customization.
 */
export abstract class MapManager {
  protected map: GeoMap | null = null
  protected mapBounds: Bounds | BoundsLiteral | null = null
  protected markerOverlayLayer?: { spiderfier: OverlappingMarkerSpiderfier } | null
  protected markerAssets: Record<string, any> = {}
  protected isAPopupOpen = false
  protected resetMapViewportOnMinZoomListener?: MapsEventListener
  protected mapFittedBoundsOnLoad = false
  protected markerIcons: MarkerColorsHash | null = null

  constructor(protected rootEl: HTMLElement, protected options: MapOptions = {}) {}

  abstract initialize(latitude: number, longitude: number, zoom: number): void
  abstract addClickListener(callback: (e: Event) => void): void
  abstract setOptions(options: MapOptions): void
  abstract getCenter(): LatLngLiteral
  abstract getBounds(): Bounds | null
  abstract setZoom(zoom: number): void
  abstract getZoom(): number
  abstract panTo(position: LatLngLiteral): void
  abstract setCenter(position: LatLngLiteral): void
  abstract fitBounds(bounds: Bounds): void
  abstract setTheme(themeId: number): void
  abstract addControl(control: HTMLElement, position: any): void
  abstract addIdleListener(callback: () => void): void
  public abstract fromLatLngToPixel(latlng: LatLngLiteral): { x: number; y: number } | null
  public abstract fromPixelToLatLng(pixel: { x: number; y: number }): LatLngLiteral | null

  protected handleBoundsChanged(): void {
    if (this.map == null || this.isAPopupOpen) return
    const bounds = this.getBounds()
    if (bounds == null) return

    // Only when map is zoomed out to the world level
    if (this.isWorldLevelZoom(bounds)) {
      let longitude = this.getCenter().lng % 360
      if (longitude < -180) longitude = 360 + longitude

      // Move the map back into center if user pans outside
      if (longitude < -90 || longitude > 90) {
        this.panTo({
          lat: this.getCenter().lat,
          lng: 0,
        })
        // Re-setup the bounds listener after panning
        this.setupBoundsListener()
      }
    }
  }

  protected setupBoundsListener(): void {
    this.resetMapViewportOnMinZoomListener?.remove()
    if (this.map == null) return

    this.resetMapViewportOnMinZoomListener = this.map.addListener(
      'bounds_changed',
      debounce(this.handleBoundsChanged.bind(this), 100),
    )
  }

  /**
   * Sets up the bounds listener to keep the map centered within one horizontal map view.
   * This ensures proper handling of the International Date Line (IDL) bounds.
   */
  protected setupFittingMapInsideIDLBounds(): void {
    this.resetMapViewportOnMinZoomListener?.remove()
    this.setupBoundsListener()
  }

  /**
   * Keeps the map centered within one horizontal map view with America on the left
   * and Asia on the right when zoomed out to world level.
   * Prevents the map from repeating across the International Date Line (IDL).
   */
  protected resetMapViewportToCenterOnMinZoom(): void {
    if (this.map == null) return
    const bounds = this.getBounds()
    if (bounds == null) return

    // Only when map is zoomed out to the world level
    if (this.isWorldLevelZoom(bounds)) {
      let longitude = this.getCenter().lng % 360
      if (longitude < -180) longitude = 360 + longitude

      // Move the map back into center if user pans outside
      if (longitude < -90 || longitude > 90) {
        this.panTo({
          lat: this.getCenter().lat,
          lng: 0,
        })
        this.resetMapViewportOnMinZoomListener?.remove()
        // Continue listening to idle event to re-setup the bounds listener
        this.addIdleListener(() => {
          this.setupBoundsListener()
        })
      }
    }
  }

  protected isWorldLevelZoom(bounds: Bounds): boolean {
    return bounds.getNorthEast().lng() === 180 && bounds.getSouthWest().lng() === -180
  }

  public setPopupMode(isPopupOpen: boolean): void {
    this.isAPopupOpen = isPopupOpen
    this.setOptions({
      restriction: isPopupOpen ? RELAXED_BOUND : RESTRICTED_BOUND,
    })
  }

  public getMap(): GeoMap | null {
    return this.map
  }

  public zoomIn(): void {
    if (this.map == null) return
    this.setZoom(this.getZoom() + 1)
  }

  public zoomOut(): void {
    if (this.map == null) return
    this.setZoom(this.getZoom() - 1)
  }

  public fitMapForPositions(positions: LatLngLiteral[], pan = false): void {
    if (this.map == null || this.rootEl == null) return

    const mapDim = this.getMapDimensions()

    // Sort positions from west to east to handle International Date Line correctly
    const sortedPositions = [...positions].sort((p1, p2) => p2.lng - p1.lng)
    this.mapBounds = getBoundsContainingPositions(sortedPositions)
    const boundsZoomLevel = getBoundsZoomLevel(this.mapBounds, mapDim)

    this.setupFittingMapInsideIDLBounds()

    this.setZoom(boundsZoomLevel)

    const center = this.mapBounds.getCenter() as unknown as LatLngLiteral
    if (pan) {
      this.panTo(center)
    } else {
      this.setCenter(center)
    }

    // Doing fitBounds because center is on the globe using lat and lng is not the center of
    // the bound rectangle. Pan to center will be off on our map with Mercator projection
    this.fitBounds(this.mapBounds)
  }

  public fitBoundsForMarkers(markers: PostMarkerObject[], defaultPosition?: LatLngLiteral & { zoom: number }): void {
    if (this.map == null) return

    const positions = getUniquePositionsFromPositions(markers.map((m) => m.position))

    if (positions.length > 1) {
      this.fitMapForPositions(positions)
    } else if (positions.length === 1) {
      this.setZoom(16)
      this.setCenter(positions[0])
    } else if (defaultPosition != null) {
      const { zoom, ...latlng } = defaultPosition
      this.setZoom(zoom)
      this.setCenter(latlng)
    }
  }

  public setMapTheme(styles: any, mapTypeId: string): void {
    this.setOptions({
      styles,
      mapTypeId,
    })
  }

  public getMarkerOverlayLayer(): { spiderfier: OverlappingMarkerSpiderfier } | null {
    return this.markerOverlayLayer ?? null
  }

  protected getMapDimensions(): MapDimensions {
    return {
      height: this.rootEl.clientHeight ?? window.innerHeight,
      width: this.rootEl.clientWidth,
    }
  }

  public getMarkerIcons(): MarkerColorsHash | null {
    return this.markerIcons
  }

  public isMapFittedBoundsOnLoad(): boolean {
    return this.mapFittedBoundsOnLoad
  }

  public setMapFittedBoundsOnLoad(value: boolean): void {
    this.mapFittedBoundsOnLoad = value
  }

  public centerMapToPost(postHeight: number): void {
    if (this.map == null) return

    const center = this.getCenter()
    const newCenterPixel = this.fromLatLngToPixel(center)
    if (newCenterPixel == null) return

    newCenterPixel.y -= postHeight / 2
    const newCenter = this.fromPixelToLatLng(newCenterPixel)
    if (newCenter != null) {
      this.panTo(newCenter)
    }
  }

  public centerMapToPostBeingEdited(postHeight: number): void {
    if (this.map == null) return

    // First center immediately
    this.centerMapToPost(postHeight)

    // Then center again after the map is idle to ensure proper positioning
    this.addIdleListener(() => {
      this.centerMapToPost(postHeight)
    })
  }
}
